import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { PartnerService } from '@feature/dashboard/partners/services/partner.service';
import { PartnerListInterface } from '@feature/dashboard/partners/types/partner-list';
import { SenderService } from '@feature/dashboard/senders/services/sender.service';
import { SenderListInterface } from '@feature/dashboard/senders/types/sender-list';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { sortDirection } from '@sharedModule/constants/data-grid.constants';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { HasAccessDirective } from '@sharedModule/directives/has-access.directive';
import { HasPlatformAccessDirective } from '@sharedModule/directives/has-platform-access.directive';
import { MaterialModule } from '@sharedModule/material/material.module';
import { ExcelExportService } from '@sharedModule/services/excel-export/excel-export.service';
import { getActiveSort } from '@sharedModule/utils/sort.utils';
import { Subject, Observable, debounceTime, takeUntil } from 'rxjs';
import {
  updatePartnerWalletListFilter,
  resetPartnerWalletList,
} from '../../store/partner-wallet-list/partner-wallet-list.actions';
import { selectPartnerWalletList } from '../../store/partner-wallet-list/partner-wallet-list.selector';
import {
  updateSenderWalletListFilter,
  resetSenderWalletList,
} from '../../store/sender-wallet-list/sender-wallet-list.actions';
import { selectSenderWalletList } from '../../store/sender-wallet-list/sender-wallet-list.selector';
import { WalletListInterface } from '../../types/wallet-list';
import {
  defaultWalletListFilter,
  WalletListFilter,
} from '../../types/wallet-list-filter';
import { AddWalletComponent } from '../add-wallet/add-wallet.component';

@Component({
  selector: 'app-wallet-list',
  standalone: true,
  imports: [
    InputModule,
    CommonModule,
    RouterModule,
    MaterialModule,
    HasAccessDirective,
    ReactiveFormsModule,
    HasPlatformAccessDirective,
  ],
  templateUrl: './wallet-list.component.html',
  styleUrl: './wallet-list.component.scss',
})
export class WalletListComponent implements OnInit, OnDestroy {
  public readonly attrConstant = ATTRIBUTES_CONSTANT.wallets;
  filterForm!: FormGroup;
  displayedColumns: string[] = ['country', 'currency', 'balance', 'created_at'];
  walletList?: WalletListInterface[];
  dataSource!: MatTableDataSource<WalletListInterface>;
  activeSort?: Sort;
  fallBackSort = {
    active: 'created_at',
    direction: sortDirection.desc,
  };
  page = 0;
  total = 0;
  limit = 10;
  subject = new Subject<{
    partner_id: number;
    sender_id: number;
  }>();
  isSuperAdmin!: boolean;
  partnerList!: Observable<PartnerListInterface[]>;
  senderList!: SenderListInterface[];
  #destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private dialogRef: MatDialog,
    private senderService: SenderService,
    private partnerService: PartnerService,
    private excelExportService: ExcelExportService,
    private authenticationService: AuthenticationService
  ) {
    this.isSuperAdmin = this.authenticationService.isSuperAdmin();
    this.displayedColumns = this.isSuperAdmin
      ? ['partner_name'].concat(this.displayedColumns)
      : ['sender_name'].concat(this.displayedColumns);
  }

  ngOnInit(): void {
    this.filterFormInit();
    this.loadData();
    this.dispatchFilter();
    if (this.isSuperAdmin) this.partnerList = this.partnerService.sharedData$;
    else this.getSenderList();
    const debounceTimeDuration = 1000;
    this.subject
      .pipe(debounceTime(debounceTimeDuration), takeUntil(this.#destroy$))
      .subscribe(res => {
        const partnerId = this.filterForm.value[this.attrConstant.partner.key];
        const senderId = this.filterForm.value[this.attrConstant.sender.key];
        this.dispatchFilter({
          count: 10,
          page: 0,
          partner_id: ['', null].includes(partnerId) ? null : [res.partner_id],
          sender_id: ['', null].includes(senderId) ? null : [res.sender_id],
        });
      });
  }

  filterFormInit(): void {
    this.filterForm = new FormGroup({
      [this.attrConstant.partner.key]: new FormControl(''),
      [this.attrConstant.sender.key]: new FormControl(''),
    });
  }

  loadData(): void {
    this.store
      .select(
        this.isSuperAdmin ? selectPartnerWalletList : selectSenderWalletList
      )
      .pipe(takeUntil(this.#destroy$))
      .subscribe(data => {
        if (data) {
          this.walletList = data.rawData;
          this.dataSource = new MatTableDataSource(data.rawData);
          this.page = data.page;
          this.total = data.totalRecords;
          this.limit = data.count;
        }
      });
  }

  getSenderList(): void {
    this.senderService
      .getSenderDropdown()
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.senderList = x.response.map((data: SenderListInterface) => {
              return Object.assign(data, {
                sender_name: `${data.first_name} ${data.middle_name} ${data.last_name}`,
              });
            });
          }
        },
      });
  }

  addWallet(): void {
    const dialogRef = this.dialogRef.open(AddWalletComponent, {
      disableClose: true,
      data: {
        senderList: this.senderList,
        partnerList: this.partnerList,
        isSuperAdmin: this.isSuperAdmin,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) this.dispatchFilter();
    });
  }

  applyFilter(): void {
    this.subject.next(this.filterForm.value);
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.dispatchFilter({ ...defaultWalletListFilter });
  }

  reload(): void {
    this.dispatchFilter();
  }

  onPageChange(pageEvent: PageEvent): void {
    this.dispatchFilter({
      count: pageEvent.pageSize,
      page: pageEvent.pageIndex,
    });
  }

  onSortChange(sortEvent: Sort): void {
    this.activeSort = getActiveSort(sortEvent, this.fallBackSort);
    this.dispatchFilter({
      sort_key: this.activeSort.active,
      sort_by: this.activeSort.direction,
    });
  }

  dispatchFilter(walletListFilter?: WalletListFilter): void {
    this.store.dispatch(
      this.isSuperAdmin
        ? updatePartnerWalletListFilter({ filter: walletListFilter ?? {} })
        : updateSenderWalletListFilter({ filter: walletListFilter ?? {} })
    );
  }

  export(): void {
    this.excelExportService.exportToExcel(this.walletList!, 'wallets');
  }

  ngOnDestroy(): void {
    this.store.dispatch(
      this.isSuperAdmin ? resetPartnerWalletList() : resetSenderWalletList()
    );
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
